// Copyright © 2023 Ashley Rust Digital, Inc. All rights reserved.

import { observer } from 'mobx-react';
import { Component } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';

//import Footer from '../components/Footer';

interface Props extends WithStyles<typeof styles> {
}

const PublicistCareerPage = observer(class PublicistCareerPage extends Component<Props> {
    componentDidMount() {
        window.scrollTo(0, 0);
    }

    render() {
        const { classes } = this.props;

        return <>
            <div className={classes.content}>
                <div className={classes.container}>
                    <div className={classes.heading}>
                        Join Our Team
                    </div>
                    <div className={classes.box}>
                        <p className={classes.role}>Publicist Contract-To-Hire</p>

                        <p className={classes.subtitle}>Who We Are:</p>
                        <p className={classes.description}>
                            ARA is a premier full-service Digital Branding and Public Relations firm serving notable individuals, CEOs, and celebrities. Headquartered in NYC, we specialize in architecting digital brands as well as providing bespoke solutions to a broad spectrum of online issues and challenges. Our job is to shape our client’s digital legacy and protect them where it matters most. We are a small (but growing) team working to build a better, digital-first PR agency.
                        </p>

                        <p className={classes.subtitle}>What We're Looking For:</p>
                        <p className={classes.description}>
                            Ashley Rust Associates seeks an experienced Publicist to join our growing team of contractors. This is a 3 month part-time contracted-to-hire role, where you are guaranteed hours and for the right candidate, we would consider hiring full-time at the end of the contract based on performance. (Alternatively we would consider quarterly contract renewals)
                        </p>
                        <p className={classes.description}>
                            This person will pitch relevant opportunities for brand recognition and awareness via media coverage on behalf of our clients and be our “go-to” freelancer to help execute the work our clients need. This person should possess the ability to generate creative ideas and help craft communications campaigns and initiatives.
                        </p>
                        <p className={classes.description}>
                            This role is ideal for an experienced PR professional who is able to work independently and efficiently, and is open to collaborating with our strategy team.
                        </p>
                        <p className={classes.description}>
                            This role is a role to support high-profile executives and their companies.
                        </p>

                        <p className={classes.subtitle}>Core Responsibilities</p>
                        <ul className={classes.list}>
                            <li>Be available M-F for inbound inquiries</li>
                            <li>Develop communications campaigns & creative campaigns to elevate client’s brand</li>
                            <li>Research client competitors, industry trending topics, media contacts/outlets</li>
                            <li>Pitch opportunities to relevant outlets to get positive PR coverage for our clients</li>
                            <li>Produce media lists and associated pitches</li>
                            <li>Secure placements in digital-first publications as well as magazines, newspapers, television, radio, and podcasts for clients</li>
                            <li>Develop media materials (e.g. Fact Sheets, Press Releases, Backgrounders)</li>
                            <li>Coordinate interview scheduling, develop interview prep, and conduct interviews</li>
                            <li>Conduct ongoing media monitoring  and reporting as needed</li>
                            <li>Spearhead press updates and relevant factual inaccuracy corrections</li>
                        </ul>

                        <p className={classes.subtitle}>Agency Specific</p>
                        <ul className={classes.list}>
                            <li>Join Zoom internal company meetings approx. 2x/ week (based on the volume of clients)</li>
                            <li>Utilize internal time-tracking software to log billable hours</li>
                            <li>Utilize ARA project management software</li>
                            <li>Potential travel (ARA/client to cover relevant costs)</li>
                        </ul>

                        <p className={classes.subtitle}><u>What you'll need</u></p>
                        <ul className={classes.list}>
                            <li>5+ years of publicist/PR experience</li>
                            <li>Prior experience working on behalf of individuals</li>
                            <li>40-50 hours of availability a month</li>
                            <li>Excellent organization and planning skills</li>
                            <li>Strong copywriting and communication skills</li>
                            <li>Desire to work independently, figure it out, and get results</li>
                            <li>Ability to manage time, multi-task and work quickly with efficiency and attention to detail while ensuring deadlines are met</li>
                        </ul>

                        <p className={classes.subtitle}>What's in it for you?</p>
                        <p className={classes.description}>
                            Get ready to make an impact on a small but growing team. We’re fully remote and work with various unexpected clients - which means no two days will be the same. You will be a key contributor and help us build processes as our client roster grows.
                        </p>
                        <p className={classes.description}>
                            If this posting speaks to you, send us an email with "Publicist" in the subject line, including your LinkedIn link to <a className={classes.emailAddress} href='mailto:info@ashleyrustassociates.com' target='_blank' rel='noopener noreferrer'>info@ashleyrustassociates.com</a>.
                        </p>
                    </div>
                </div>
            </div>
            {/*<Footer />*/}
        </>;
    }
});

const styles = (theme: Theme) => createStyles({
    content: {
        paddingTop: 60,
        paddingBottom: 60,
        color: '#fff',
        height: '100vh',
        '@media (max-width: 767px)': {
            paddingTop: 40,
            paddingBottom: 40
        }
    },
    container: {
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '80%',
        maxWidth: 800,
        alignItems: 'center',
        '@media (max-width: 767px)': {
            width: '90%',
            paddingLeft: 0,
            paddingRight: 0
        }
    },

    heading: {
        fontSize: 31,
        fontWeight: 500,
        paddingBottom: 16,
        marginBottom: 40,
        marginTop: 60,
        letterSpacing: 4,
        lineHeight: 1.4,
        fontFamily: 'Questrial',
        textTransform: 'uppercase',
        textAlign: 'center'
    },

    box: {
        fontFamily: 'Questrial',
        paddingBottom: 80
    },
    link: {
        color: 'rgb(241, 42, 69)'
    },
    hairline: {
        marginTop: 32,
        height: 0.5,
        background: '#ccc'
    },
    role: {
        fontSize: 21,
        fontWeight: 600,
        marginTop: 48,
        marginBottom: 16,
        letterSpacing: 2,
        textTransform: 'uppercase'
    },
    subtitle: {
        fontSize: 19,
        fontWeight: 600,
        marginBottom: 0
    },
    description: {
        marginTop: 6,
        marginBottom: 12,
        fontSize: 19,
        lineHeight: 1.4,
        color: '#ccc'
    },
    list: {
        paddingLeft: 28,
        '& li': {
            lineHeight: 1.5,
            fontSize: 19
        }
    },
    label: { 
        fontSize: 19,
        marginRight: 3,
        lineHeight: 1.5,
        color: '#fff'
    },
    emailAddress: {
        fontSize: 19,
        color: '#fff',
        letterSpacing: 1.1,
        textDecoration: 'none',
        padding: 1,
        borderBottom: '1.5px solid rgba(255, 255, 255, 0.2)',
        '&:hover': {
            textDecoration: 'none',
            borderBottom: '1.5px solid rgba(255, 255, 255, 0.35)'
        }
    }
});

export default withStyles(styles)(PublicistCareerPage);
